<template>
  <div class="w-24 h-24">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import type { ChartDataset } from 'chart.js';

// Register Chart.js components
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const chartCanvas = ref<HTMLCanvasElement | null>(null);
let chartInstance: Chart<'doughnut', number[], string> | null = null;

const props = defineProps<{
  labels: string[]
  values: number[]
  colors: string[]
}>();

const createChart = () => {
  if (!chartCanvas.value) return;

  // Destroy existing chart if any
  chartInstance?.destroy();

  chartInstance = new Chart(chartCanvas.value, {
    type: 'doughnut',
    data: {
      labels: props.labels,
      datasets: [
        {
          data: props.values,
          backgroundColor: props.colors,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false,
        },
      },
    },
  });
};

onMounted(createChart);

watch(
  () => ({
    labels: props.labels,
    values: props.values,
    colors: props.colors,
  }),
  ({ labels, values, colors }) => {
    if (!chartInstance) return;

    const dataset: ChartDataset<'doughnut', number[]> | undefined = chartInstance.data.datasets[0];
    if (!dataset) return;

    chartInstance.data.labels = labels.map(String);
    dataset.data = [...values]; // ✅ Explicitly ensure it's number[]
    dataset.backgroundColor = colors.map(String);

    chartInstance.update();
  },
  { deep: true },
);

onBeforeUnmount(() => {
  chartInstance?.destroy();
  chartInstance = null;
});
</script>

<style scoped>
canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
